import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import braze from 'src/_shared/braze';
import { useStore } from 'src/contexts/StoreContext';
import {
  DYNAMIC_MODAL_ACTIONS,
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import useResponsiveScreen from './useResponsiveScreen';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import { TOAST_IDS } from '@photosi/rn-components/toast/Toast.constants';
import { PRODUCT_FAMILY_CODES } from 'src/constants/code.constants';
import { useRoute } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';

enum AddToCartChecks {
  INITIAL = 0,
  BLANKS = 1,
  MAX_PRODUCTS = 2,
  MIN_PRODUCTS = 3,
  QUALITY = 4,
  CROPS = 5,
  UPSELLING = 6,
}

/**
 * Hook that provides handlers for adding products to the cart.
 * @returns an object with the handleAddToCart and handleClickAddToCart functions.
 */
export const useAddToCartHandlers = (isNewEditor = false) => {
  const {
    project: { isProjectAddedToCart, activeProduct, totalNumberOfProducts, activeProject, addToCart },
    dynamicModal: { openModal },
    bottomSheet: { openBottomSheet },
    toastStore: { hideToast },
  } = useStore();
  const route = useRoute();
  const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);
  const { isMobile } = useResponsiveScreen();

  /**
   * Opens the UploadToCart modal and adds the current project to the cart.
   */
  const handleAddToCart = () => {
    if (!activeProject || totalNumberOfProducts === 0) {
      if (isProjectAddedToCart) {
        addToCart(newRoute);
      }
    } else {
      openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadToCart,
      });
      amplitude.trackEvent(AnalyticsEvents.personalize.continueClick, {
        n_products: totalNumberOfProducts,
        n_photos: activeProject?.totalNumberOfUserImages ?? 0,
        n_photos_facebook: 0,
        n_photos_gphoto:
          activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.GooglePhoto)
            ?.length ?? 0,
        n_photos_local:
          activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Computer)
            ?.length ?? 0,
        n_photos_smartphone:
          activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Smartphone)
            ?.length ?? 0,
        n_photos_family_friends:
          activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.FamilyFriends)
            ?.length ?? 0,
      });
      braze.trackEvent(AnalyticsEvents.personalize.continueClick);
      addToCart(newRoute);
    }
  };

  const handleClickAddToCart = async () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.addToCartClick, {
      n_products: totalNumberOfProducts,
      n_photos: activeProject?.totalNumberOfUserImages ?? 0,
      n_photos_facebook: 0,
      n_photos_gphoto:
        activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.GooglePhoto)
          ?.length ?? 0,
      n_photos_local:
        activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Computer)
          ?.length ?? 0,
      n_photos_smartphone:
        activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Smartphone)
          ?.length ?? 0,
      n_photos_family_friends:
        activeProject?.selectedThumbnails.filter(thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.FamilyFriends)
          ?.length ?? 0,
    });
    braze.trackEvent(AnalyticsEvents.personalize.addToCartClick);
    return checkAddToCartControls(AddToCartChecks.INITIAL);
  };

  /**
   * Handles the add to cart button click.
   * If there are no products, do nothing.
   * If there are any blank spaces, open the empty images modal.
   * If there are any quality alerts, open the quality alert modal with the quality options.
   * If there is only one product, open the upselling modal if there is upselling info, otherwise add the product to the cart.
   * If there are more than one product, add the products to the cart.
   */
  const checkAddToCartControls = async (state: AddToCartChecks = AddToCartChecks.INITIAL) => {
    if (!activeProject || totalNumberOfProducts === 0) {
      if (isProjectAddedToCart) {
        handleAddToCart();
      }
    } else if (activeProject.isAnyBlankSpace) {
      openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.EmptyImages,
        afterInit: () => amplitude.trackEvent(AnalyticsEvents.personalize.emptySpaceAlertView),
      });
    } else if (activeProject.isMissingMaxProducts && state < AddToCartChecks.MAX_PRODUCTS) {
      const firstGroupWithMissingProducts = activeProject.groups.find(group => group.isMissingMaxProducts)!;
      openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.MaxProductsAlert,
        productLimit: firstGroupWithMissingProducts.maxItems,
        groupWithLimit: firstGroupWithMissingProducts.code,
      });
    } else if (activeProject.isMissingMinProducts && state < AddToCartChecks.MIN_PRODUCTS) {
      const firstGroupWithMissingProducts = activeProject.groups.find(group => group.isMissingMinProducts)!;
      openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.MinProductsAlert,
        productLimit: firstGroupWithMissingProducts.minItems,
        groupWithLimit: firstGroupWithMissingProducts.code,
      });
    } else if (activeProject.hasAnyImageWithCropAlert && isNewEditor && state < AddToCartChecks.CROPS) {
      const trackingParams = {
        mode: 'single_product',
        product_group: 'PRINT_CLASSIC',
        section_name: SECTION_NAMES.CUT_ALERT_MODAL,
        source: 'cart_add',
        text_id: 'PRINT_CLASSIC',
      };
      openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.CutAlert,
        actionCallback: (action: DYNAMIC_MODAL_ACTIONS) => {
          switch (action) {
            case DYNAMIC_MODAL_ACTIONS.REVIEW:
              amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
                ...trackingParams,
                option: 'review',
              });
              openModal({ dynamicModalView: DYNAMIC_MODAL_VIEWS.AdjustCrops });
              break;
            case DYNAMIC_MODAL_ACTIONS.CONTINUE:
              amplitude.trackEvent(AnalyticsEvents.personalize.qualityAlertOptionClick, {
                ...trackingParams,
                option: 'continue',
              });
              hideToast(TOAST_IDS.CutsCheckAlert);
              checkAddToCartControls(AddToCartChecks.CROPS);
              break;
            default:
              // do nothing
              break;
          }
        },
      });
    } else if (
      totalNumberOfProducts === 1 &&
      activeProject.products[0].family === PRODUCT_FAMILY_CODES.HOMEDECOR &&
      state < AddToCartChecks.UPSELLING
    ) {
      // TODO: analizzare in futuro il caso stampe
      const upsellingInfo = await activeProject.getUpsellingInfo();
      // upsellingInfo is null if the cloud function API returns a 404.
      // Empty if upselling exists but cannot be proposed due to quality issues.
      if (upsellingInfo === null || upsellingInfo?.upsellingData?.length === 0) {
        checkAddToCartControls(AddToCartChecks.UPSELLING);
      } else {
        amplitude.trackEvent(AnalyticsEvents.upselling.view);
        const onPressSaveChanges = () => {
          activeProduct
            ?.applyLookup(upsellingInfo?.upsellingData?.at(0)?.upsellingProductId ?? '')
            .then(() => checkAddToCartControls(AddToCartChecks.UPSELLING));
        };
        if (isMobile) {
          openBottomSheet({
            bottomSheetView: BOTTOM_SHEET_VIEWS.Upselling,
            bottomSheetData: upsellingInfo,
            onPressDiscardChanges: () => checkAddToCartControls(AddToCartChecks.UPSELLING),
            onPressSaveChanges,
          });
        } else {
          openModal({
            dynamicModalView: DYNAMIC_MODAL_VIEWS.ModalUpSelling,
            upSellingModalData: upsellingInfo,
            onPressDiscardChanges: () => checkAddToCartControls(AddToCartChecks.UPSELLING),
            onPressSaveChanges,
          });
        }
      }
    } else {
      handleAddToCart();
    }
  };

  return {
    handleAddToCart,
    handleClickAddToCart,
  };
};
